'use client';

import { revalidateBatches } from '@/api/batch';
import { getOrder, verifyOrder } from '@/api/payment';
import { useToast } from '@/hooks/use-toast';
import { Env } from '@/lib/Env';
import { isActionError } from '@/lib/error';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useIsMobile } from './use-mobile';
interface UseRazorpayPaymentOptions {
  batchId?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  userDetails?: {
    name?: string;
    email?: string;
    contact?: string;
  };
}
interface PaymentError {
  code?: string;
  description?: string;
  message?: string;
  source?: 'razorpay' | 'internal';
}
export function useRazorpayPayment({
  batchId,
  onSuccess,
  onError,
  userDetails
}: UseRazorpayPaymentOptions) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const handleSuccessfulPurchase = async (showAdmissionForm?: boolean) => {
    setIsLoading(false);
    await queryClient.invalidateQueries({
      queryKey: ['purchasedBatches']
    });
    toast({
      description: <div className="flex items-center gap-2">
          <div className="rounded-full bg-green-500 p-1">
            <Check className="size-4 text-white" />
          </div>
          <span>Batch Successfully purchased</span>
        </div>,
      variant: 'default'
    });
    onSuccess?.();
    if (showAdmissionForm) {
      router.refresh();
      router.push('/admissionform');
      return;
    }
    if (isMobile) {
      return router.replace('/profile/purchased-batches');
    } else {
      const event = new CustomEvent('open-profile-sheet', {
        detail: {
          activeTab: 'My batches'
        }
      });
      window.dispatchEvent(event);
      router.back();
    }
    await revalidateBatches();
  };
  const handlePaymentError = (error: PaymentError) => {
    setIsLoading(false);
    let errorMessage = 'Payment failed. Please try again.';
    if (error.source === 'razorpay') {
      switch (error.code) {
        case 'BAD_REQUEST_ERROR':
          errorMessage = 'Invalid payment details provided';
          break;
        case 'PAYMENT_CANCELLED':
          errorMessage = 'Payment was cancelled';
          break;
        case 'NETWORK_ERROR':
          errorMessage = 'Network error occurred. Please check your internet connection';
          break;
        case 'EXTERNAL_WALLET_CANCELED':
          errorMessage = 'External wallet payment was cancelled';
          break;
        case 'INVALID_EMAIL_OR_PHONE':
          errorMessage = 'Please provide valid contact details';
          break;
        default:
          errorMessage = error.description || error.message || errorMessage;
      }
    } else {
      errorMessage = error.message || errorMessage;
    }
    toast({
      description: errorMessage,
      variant: 'destructive'
    });
    onError?.(error);
  };
  const processPayment = async () => {
    if (!batchId) {
      toast({
        description: 'Batch ID is required',
        variant: 'destructive'
      });
      return;
    }
    try {
      setIsLoading(true);
      const order = await getOrder(batchId);
      if (isActionError(order)) {
        throw new Error(order.error);
      }
      if (order?.status === 'Captured') {
        await handleSuccessfulPurchase();
        return;
      }
      const orderId = order?.id;
      if (!orderId) {
        handlePaymentError({
          message: 'Failed to create order',
          source: 'internal'
        });
        return;
      }
      const options = {
        key: Env.NEXT_PUBLIC_RAZORPAY_PUBLIC_KEY,
        currency: order.currency,
        order_id: orderId,
        name: 'ELT GLOBAL',
        image: 'https://strategy.eltglobal.in/wp-content/uploads/2024/12/eltglobal-app-icon.svg',
        prefill: {
          name: userDetails?.name || '',
          email: userDetails?.email || '',
          contact: userDetails?.contact || ''
        },
        config: {
          display: {
            hide: [{
              method: 'emi'
            }, {
              method: 'wallet'
            }, {
              method: 'paylater'
            }],
            preferences: {
              show_default_blocks: true
            }
          }
        },
        async handler(response: any) {
          try {
            setIsLoading(false);
            if (!response.razorpay_order_id || !response.razorpay_payment_id) {
              throw new Error('Invalid Payment Response');
            }
            const data = {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature
            };
            const result = await verifyOrder(data);
            if (isActionError(result)) {
              throw new Error(result.error);
            }
            if (result?.order) {
              await handleSuccessfulPurchase(result.shouldSubmitAdmissionForm);
            } else {
              throw new Error('Order verification failed');
            }
          } catch (error: any) {
            handlePaymentError({
              message: error.message,
              source: 'internal'
            });
          }
        },
        modal: {
          ondismiss() {
            setIsLoading(false);
            toast({
              description: 'Payment window was closed',
              variant: 'destructive'
            });
          }
        },
        theme: {
          color: '#3399cc'
        }
      };
      const paymentObject = new (window as any).Razorpay(options);
      paymentObject.on('payment.failed', (response: any) => {
        handlePaymentError({
          ...response.error,
          source: 'razorpay'
        });
      });
      paymentObject.on('payment.error', (errorData: any) => {
        handlePaymentError({
          message: errorData?.description || 'Payment processing failed',
          code: errorData?.code,
          source: 'razorpay'
        });
      });
      paymentObject.open();
      setIsLoading(false);
    } catch (error: any) {
      handlePaymentError({
        message: error.message,
        source: 'internal'
      });
    }
  };
  return {
    processPayment,
    isLoading
  };
}